import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import Helmet from 'react-helmet';
import LazyLoad from 'react-lazy-load';
import { provideHooks } from 'redial';
import { browserHistory } from 'react-router';
import classNames from 'classnames';

import { actions as articleActions } from '@spp/redux-article';
import { appConfig as config } from 'roc-package-web-app-react/app/shared/universal-config';
import ArticleComponent from '@spp/article';
import irisUrls from '@spp/iris-urls';
import skin from '@vg/cn-skin-vg';
import { reduxActions } from '@spp/tracking';

import { actions as coreNavActions } from '@spp/ui-core-navigation';
import { themer } from '@spp/theme-provider';
import CouponCodes from '@vg/coupon-codes';
import ExternalArticles from '@vg/external-articles';
import List from '../../local-components/List';
import SchemaOrgArticle from '../../local-components/SchemaOrg';
import GenericError from '../error';

import deferOnload from '../../utils/defer-onload';
import initSpid from '../../utils/init-spid';
import loadCogwheel from '../../ads/load-cogwheel';
import { SponsorAd, ClientPlacedAd, AdPlacement, HorseshoeAd } from '../../local-components/Ads';
import { fetchRecirculationListAction } from '../../redux/actions/recirculation';
import { fetchCouponCodesAction } from '../../redux/actions/coupon-codes';
import { utmParamsAction } from '../../redux/actions/utm-params';
import redirectArticleRoute from '../../utils/generate-article-route';
import generateLpMeta from '../../utils/get-lp-meta';
import getLeadText from '../../utils/get-lead-text';
import localComponentsMapping from './article-component-mapping';
import NorskTippingMeta from '../../local-components/NorskTippingMeta';
import ShareMeta from '../../local-components/ShareMeta';
import getUTMtags from '../../utils/get-utm-tags';
import clientParams from '../../tracking/client-params';
import { getFonts, fontsToInlineStyle } from '../../utils/font-chooser';

const urls = irisUrls(config.iris); // eslint-disable-line

const {
    iris: { host },
    articleList: {
        recirculation: { endpoint: recirculationEndpoint },
    },
    couponCodes: { endpoint: couponCodesEndpoint },
    baseUrl,
} = config;

const recirculationUtmParams = {
    utm_content: 'recirculation-matrix',
};

// https://github.schibsted.io/spt-dataanalytics/event-sdk-js/blob/master/autotracking/click.js
const trackRecirculationClick = (e) => {
    if (!window.AutoTrack) {
        return;
    }

    const targetArticleId = e.currentTarget.getAttribute('data-article-id');
    const activity = window.AutoTrack.activity();
    activity.events
        .trackClick('recirculation-matrix', undefined, 'Article', targetArticleId)
        .send();
};

@provideHooks({
    fetch: ({ params, dispatch, force, location, setProps }) => {
        const { id: articleId } = params;
        const { query: { layout } = {} } = location;

        if (layout) {
            setProps({ layout });
        }

        const fetchArticle = articleActions.fetchArticle({
            id: articleId,
            endpoint: urls.article(articleId, layout),
            force,
            headers: {
                'X-SP-Bypass': 'Hyperion',
            },
        });

        const fetchCouponCodes = fetchCouponCodesAction({
            endpoint: couponCodesEndpoint,
        });

        return Promise.all([dispatch(fetchArticle), dispatch(fetchCouponCodes)]).then(() =>
            redirectArticleRoute(dispatch, location, params),
        );
    },
    defer: ({ dispatch, location, getProps }) =>
        dispatch(async (thunkDispatch, getState) => {
            const { search = '', pathname = '', query = {} } = location;
            const { layout } = getProps();
            const {
                article: { id },
            } = getState();

            recirculationUtmParams.utm_source = id;

            /* Extract all url params that are UTM tags on client and then strip them from the url */
            const utmTags = getUTMtags(search);
            await thunkDispatch(utmParamsAction(utmTags));

            const filteredQueryString = Object.keys(query)
                // Remove layout-parameter from url.
                .filter((key) => key !== 'layout')
                .map((key) => `${key}=${query[key]}`)
                .join('&');

            let filteredUrl = pathname;

            if (filteredQueryString.length > 0) {
                filteredUrl += `?${filteredQueryString}`;
            }

            browserHistory.replace(filteredUrl);

            await initSpid({ thunkDispatch, location, getState });
            thunkDispatch(coreNavActions.coreNavigationUI.init(skin.sectionsMenu));

            await deferOnload();

            loadCogwheel();

            const trackingOpt = clientParams({
                config,
                layout,
                state: getState(),
                utm: utmTags,
            });

            if (trackingOpt && !id.includes('@preview')) {
                thunkDispatch(reduxActions.trackerMultipleStart(trackingOpt));
            }
        }),
})
@connect((state) => ({
    article: state.article,
    theme: state.theme,
    recirculation: state.recirculation,
    couponCodes: state.couponCodes,
    device: state.device,
}))
@themer('Article')
class Article extends Component {
    /* eslint-disable react/forbid-prop-types */
    static propTypes = {
        theme: PropTypes.object,
        article: PropTypes.object,
        recirculation: PropTypes.object,
        couponCodes: PropTypes.object,
        dispatch: PropTypes.func.isRequired,
        device: PropTypes.string,
        layout: PropTypes.string,
    };
    /* eslint-enable react/forbid-prop-types */

    static defaultProps = {
        theme: {},
        article: {},
        recirculation: {},
        couponCodes: [],
        device: 'desktop',
        layout: '',
    };

    getRecirculationData() {
        const {
            id,
            section: { id: sectionId },
        } = this.props.article;

        const endpoint = `${host}${recirculationEndpoint}?articleId=${id}&sectionId=${sectionId}`;

        this.props.dispatch(
            fetchRecirculationListAction({
                endpoint,
            }),
        );
    }

    renderWorldCupHeader() {
        const {
            article: { tags },
        } = this.props;

        if (tags.find((tag) => tag.id === '4caba6ad-b909-4059-9ad1-2f2547b575bb') !== undefined) {
            return (
                <iframe
                    src="https://www.vg.no/spesial/2018/fotball-vm/embeds/article-stripe.php"
                    scrolling="no"
                    frameBorder="0"
                    style={{
                        width: '100%',
                        height: '50px',
                        marginBottom: '-4px',
                    }}
                />
            );
        }

        return null;
    }

    render() {
        // eslint-disable-next-line react/prop-types
        const { article, theme, recirculation, couponCodes, device, layout } = this.props;

        if (article.error) {
            const { status } = article;

            return <GenericError statusCode={status} theme={theme} />;
        }

        const isApp = device.indexOf('-app') > -1;
        const isMobile = device.indexOf('mobile') > -1;

        const {
            articleList: {
                recirculation: { title: recirculationTitle, description: recirculationDes },
            },
            head: { titleSuffix },
            adPlacements,
        } = config;

        const {
            title: { value: articleTitle = '' },
            section: { title: sectionTitle },
            links: { canonicalUrl },
            customProperties: {
                vg_paywall: isSubscription,
                googleTitle = '',
                vg_showAds: showAds,
                vg_blockSearchEngineIndexing: blockSearchEngineIndexing = false,
            },
            tags,
            id,
        } = article;

        const title = googleTitle || articleTitle;
        const leadText = getLeadText(article);

        const titleTagText = `${title.trim()} - ${titleSuffix}`;

        const {
            clientProperties: { clientPlacedAds = [], externalArticles = [], themes } = {},
        } = article;
        const hasExternalArticles = externalArticles.length > 0;
        const adTypes = isMobile ? 'mobile' : 'default';
        const keywords = tags.map((tag) => tag.title);
        const classes = classNames(theme.contentContainer, {
            [theme.noA]: !showAds, // Class name "noA" so we don't get caught by adblockers
        });
        const rootClassNames = classNames(theme.root, {
            'is-app': isApp,
        });

        if (article.section.parent) {
            keywords.push(article.section.parent.title);
        }
        const isPreview = id.includes('@preview');

        keywords.push(article.section.title);

        const fonts = getFonts(config.fonts, themes);
        const lpMeta = generateLpMeta({ baseUrl, sectionTitle, article, layout });
        const fbMeta = [
            {
                property: 'fb:pages',
                content: '137059286994', // VG
            },
            {
                property: 'fb:pages',
                content: '191842593486', // Rampelys
            },
            {
                property: 'fb:pages',
                content: '161048627113', // Sporten
            },
            {
                property: 'fb:pages',
                content: '162476410432473', // Meninger
            },
        ];

        return (
            <div className={rootClassNames}>
                <SponsorAd clientPlacedAds={clientPlacedAds} />

                <Helmet>
                    <title>{titleTagText}</title>
                    <link rel="canonical" href={canonicalUrl} />
                    <meta name="robots" content="noarchive" />
                    <meta name="description" content={leadText} />
                    <meta name="keywords" content={keywords.join(', ')} />
                    {blockSearchEngineIndexing && <meta name="robots" content="noindex" />}
                    {isSubscription && <meta name="lp:type" content="article_subscription" />}
                    {isPreview && <meta name="robots" content="noindex, nofollow" />}
                    {fonts.map(({ cdn }) => (
                        <link
                            rel="preload"
                            as="font"
                            type="font/woff2"
                            href={`${cdn}.woff2`}
                            crossOrigin
                        />
                    ))}
                    <style>{fontsToInlineStyle(fonts)}</style>
                </Helmet>
                <Helmet meta={lpMeta.concat(fbMeta)} />
                <ShareMeta article={article} />
                <SchemaOrgArticle article={article} />
                <NorskTippingMeta article={article} />

                {!isMobile && <HorseshoeAd clientPlacedAds={clientPlacedAds} device={device} />}
                <main className={classes} role="main" id="main">
                    <ClientPlacedAd subtype="test_1" clientPlacedAds={clientPlacedAds} />
                    {/* Only temporary frame, should be removed after the World Cup */}
                    {this.renderWorldCupHeader()}
                    <span className="lp_is_start" style={{ width: '1px', height: '1px' }} />
                    <ArticleComponent
                        data={article}
                        localComponentsMapping={localComponentsMapping}
                    />
                    <span className="lp_is_end" style={{ width: '1px', height: '1px' }} />

                    <div id="recirculation" data-engage-entity-id="recirculation-matrix">
                        <LazyLoad
                            offset={500}
                            debounce={false}
                            throttle={100}
                            onContentVisible={() => this.getRecirculationData()}
                        >
                            <List
                                articles={recirculation.items}
                                title={recirculationTitle}
                                description={recirculationDes}
                                onClick={trackRecirculationClick}
                                utmParams={recirculationUtmParams}
                            />
                        </LazyLoad>
                    </div>

                    <AdPlacement types={adPlacements[adTypes]} clientPlacedAds={clientPlacedAds} />

                    {hasExternalArticles && (
                        <div id="external-articles">
                            <LazyLoad offset={500} debounce={false} throttle={100}>
                                <ExternalArticles articles={externalArticles} />
                            </LazyLoad>
                        </div>
                    )}
                    <div id="coupon-codes">
                        <CouponCodes discounts={couponCodes.items} />
                    </div>
                </main>
            </div>
        );
    }
}

export default Article;



/** WEBPACK FOOTER **
 ** ./src/screens/article/index.js
 **/